import { useMemo, useDeferredValue, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X, Maximize2, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useControlPosture } from '@/api/hooks/useCompliance';
import type { ControlPostureRow, ControlPostureResult } from '@/api/types/compliance.types';
import {
  PANEL, EYEBROW, MetaChip, NOT_ASSESSED, UNASSESSED,
  axisSummary, AssessmentCard, FunctionsCard, RequirementsCard, ControlHeading,
} from '@/features/compliance/controls/control-detail';

/**
 * CONTROLS — CC Increment 1 S4.
 *
 * The tenant's Controls workspace in its first functioning state: it begins as the
 * assessment/catalogue surface and grows into the full workflow over later increments, so this
 * is the FINAL geometry with the not-yet-built regions left out — not a separate page that
 * something else replaces.
 *
 * ⚠ WHAT IS ABSENT, AND WHY IT IS ABSENT RATHER THAN PLACEHELD. Workflow (holder state,
 * assignee, verifier, due dates), evidence, policy links, questions and bulk actions are NOT
 * reachable from the canonical spine: the assignment and evidence models hang off pol_controls,
 * and there is no pol↔mcc control-level mapping (0 identifier overlap). Rendering "Unassigned"
 * on every row, an empty Evidence card, a "0 scoped out", or checkboxes wired to nothing would
 * each assert a capability that does not exist.
 *
 * ⚠ THE ROW IS SELECTION-READY BY GEOMETRY, NOT BY AFFORDANCE. Its leading gutter and the
 * reserved right edge of line 1 are where the bulk checkbox and the workflow status pill go.
 * Adding them later is a slot-in, not a redesign — but neither is drawn now, because a
 * checkbox implies bulk operations and none can be honoured yet.
 *
 * ⚠ THE HONESTY BAR. Nothing unmeasured is drawn as a zero, a pass, or a failure. A null score
 * is "nothing measured yet"; a real 0 is "measured, and failing". Opposite facts, kept apart.
 *
 * List state (search, filters, selection) lives in the query string so the full-page detail
 * route can return the user to the exact working set.
 */

const DIVIDER = 'border-border-subtle';

// ── Coverage band ─────────────────────────────────────────────────────────────────────

/**
 * ⚠ EVERY FIGURE IS SOURCED, and the closing sentence exists so the percentages are not read
 * as a verdict on the whole catalogue. There is deliberately no "scoped out" term: no
 * out-of-scope state exists here, and a constant 0 would assert a mechanism with no column.
 */
function CoverageBand({ summary, naCount }: { summary: ControlPostureResult['summary']; naCount: number }) {
  const axes = [
    ['Implementation', summary.implementation],
    ['Design', summary.designEffectiveness],
    ['Operating', summary.operatingEffectiveness],
  ] as const;
  const assessed = summary.implementation.inDenominator;

  return (
    <div className={PANEL}>
      <div className="flex flex-wrap items-stretch gap-y-[12px] px-[16px] py-[13px]">
        <div className="flex min-w-[200px] flex-col justify-center gap-[4px] pr-[26px]">
          <span className={EYEBROW}>Assessment coverage</span>
          {assessed > 0 ? (
            <span className="flex items-baseline gap-[6px] tabular-nums">
              <span className="text-[24px] font-bold leading-none text-text-primary">{assessed}</span>
              <span className="text-[12px] font-semibold text-text-muted">of {summary.controlCount} assessed</span>
            </span>
          ) : (
            <span className={cn('text-[14px] font-semibold leading-[24px]', UNASSESSED)}>None assessed yet</span>
          )}
        </div>

        <div className={cn('flex flex-wrap items-center gap-x-[26px] gap-y-[12px] border-l pl-[26px]', DIVIDER)}>
          {axes.map(([label, axis]) => (
            <div key={label} className="flex flex-col gap-[4px]">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-text-faint">{label}</span>
              {axis.score !== null ? (
                <span className="flex items-baseline gap-[7px] tabular-nums">
                  <span className="text-[17px] font-bold leading-none text-text-primary">
                    {Math.round(axis.score * 100)}<span className="text-[11px] font-bold text-text-secondary">%</span>
                  </span>
                  <span className="text-[10.5px] text-text-faint">
                    {axis.inDenominator} of {axis.inScope + naCount}
                    {axis.uncredited > 0 && <> · {axis.uncredited} not yet assessed</>}
                  </span>
                </span>
              ) : (
                // A 0% here would claim everything was measured and everything failed.
                <span className="flex items-baseline gap-[7px]">
                  <span className={cn('text-[12px] font-semibold leading-[17px]', UNASSESSED)}>{NOT_ASSESSED}</span>
                  {axis.uncredited > 0 && (
                    <span className="text-[10.5px] tabular-nums text-text-faint">{axis.uncredited} not yet assessed</span>
                  )}
                </span>
              )}
            </div>
          ))}

          {naCount > 0 && (
            <div className={cn('flex flex-col gap-[4px] border-l pl-[26px]', DIVIDER)}>
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-text-faint">Not applicable</span>
              <span className="text-[17px] font-bold leading-none tabular-nums text-text-muted">{naCount}</span>
            </div>
          )}
        </div>
      </div>

      <p className={cn('border-t px-[16px] py-[8px] text-[10.5px] leading-[1.55] text-text-faint', DIVIDER)}>
        The percentages describe only the controls assessed on each axis — not the rest of the
        catalogue, which remains unmeasured. Controls marked not applicable carry no posture and are
        never counted as assessed.
      </p>
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────────────

function Row({
  row, selected, onSelect, innerRef,
}: {
  row: ControlPostureRow; selected: boolean; onSelect: () => void;
  innerRef?: React.Ref<HTMLButtonElement>;
}) {
  const summary = axisSummary(row);
  return (
    <button
      ref={innerRef}
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={cn(
        'group relative flex w-full items-start gap-[11px] border-b px-[16px] py-[12px] text-left', DIVIDER,
        'transition-colors hover:bg-bg-surface-2/70 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-sky',
        selected && 'bg-bg-surface-2',
      )}
    >
      {/* Selected state: an accent edge plus a lift in surface — legible without a saturated fill. */}
      <span
        className={cn('absolute inset-y-0 left-0 w-[3px] transition-colors',
          selected ? 'bg-accent-sky' : 'bg-transparent group-hover:bg-cert-neutral-border')}
        aria-hidden
      />

      <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
        {/* LINE 1 — reference · title · [right edge RESERVED for the Increment-5 status pill]. */}
        <span className="flex min-w-0 items-baseline gap-[9px]">
          <span className="shrink-0 font-mono text-[11px] font-bold text-text-secondary">{row.publicId ?? '—'}</span>
          <span className={cn('min-w-0 flex-1 truncate text-[13px] font-semibold',
            selected ? 'text-text-primary' : 'text-text-primary/95')}>
            {row.title ?? 'Untitled control'}
          </span>
        </span>

        <span className={cn('truncate text-[11.5px] leading-[1.5]', row.description ? 'text-text-muted' : UNASSESSED)}>
          {row.description ?? 'No description recorded'}
        </span>

        {/* LINE 3 — neutral metadata, then the assessment summary at the right edge. */}
        <span className="flex min-w-0 flex-wrap items-center gap-[9px]">
          {row.functions.length > 0 && (
            <span className="flex shrink-0 items-center gap-[5px] whitespace-nowrap text-[10.5px] text-text-secondary">
              <span className="text-text-faint">Fn</span>
              {/* ⚠ ALL functions — a control commonly serves several; naming one "primary" would
                  be a fabrication. */}
              {row.functions.slice(0, 2).join(' · ')}
              {row.functions.length > 2 && (
                <span className="text-text-faint" title={row.functions.join(' · ')}>+{row.functions.length - 2}</span>
              )}
            </span>
          )}
          {row.functions.length > 0 && row.requiringFrameworks.length > 0 && (
            <span className="h-[10px] w-px shrink-0 bg-cert-neutral-border" aria-hidden />
          )}
          {row.requiringFrameworks.slice(0, 3).map((f) => <MetaChip key={f.key}>{f.name}</MetaChip>)}
          {row.requiringFrameworks.length > 3 && (
            <MetaChip title={row.requiringFrameworks.map((f) => f.name).join(', ')}>
              +{row.requiringFrameworks.length - 3}
            </MetaChip>
          )}
          {row.requiringFrameworks.length === 0 && (
            // Secondary metadata — accurate, and never louder than the control itself.
            <span className="truncate text-[10.5px] italic text-text-faint">
              No enabled framework currently requires this
            </span>
          )}

          <span className="ml-auto flex shrink-0 items-baseline gap-[6px] whitespace-nowrap">
            <span className={cn('text-[10.5px] font-semibold', summary.tone)}>{summary.text}</span>
            {summary.trailing && <span className="text-[10.5px] text-text-faint">· {summary.trailing}</span>}
          </span>
        </span>
      </span>
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────────────

const CONTROL_KEY = { q: 'q', fn: 'fn', fw: 'fw', sel: 'sel' } as const;

export function ControlsPage() {
  const { data, isLoading, isError } = useControlPosture();
  const [params, setParams] = useSearchParams();

  const query = params.get(CONTROL_KEY.q) ?? '';
  const fn = params.get(CONTROL_KEY.fn) ?? '';
  const framework = params.get(CONTROL_KEY.fw) ?? '';
  const selectedId = params.get(CONTROL_KEY.sel);
  const deferredQuery = useDeferredValue(query);

  const controls = data?.controls ?? [];

  const frameworks = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const c of controls) for (const f of c.requiringFrameworks) byKey.set(f.key, f.name);
    return [...byKey.entries()].map(([key, name]) => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [controls]);
  const functions = useMemo(() => [...new Set(controls.flatMap((c) => c.functions))].sort(), [controls]);

  const filtered = useMemo(() => {
    // ⚠ Search covers only fields the canonical spine holds — reference, title, description.
    // It must not imply reach into evidence, owners or policies.
    const q = deferredQuery.trim().toLowerCase();
    return controls.filter((c) => {
      if (framework && !c.requiringFrameworks.some((f) => f.key === framework)) return false;
      // Multi-valued match-if-any: a control serving several functions matches each.
      if (fn && !c.functions.includes(fn)) return false;
      if (!q) return true;
      return (c.publicId ?? '').toLowerCase().includes(q)
        || (c.title ?? '').toLowerCase().includes(q)
        || (c.description ?? '').toLowerCase().includes(q);
    });
  }, [controls, framework, fn, deferredQuery]);

  /**
   * ⚠ SELECTION-SAFETY. Selection is held by IDENTITY and dropped whenever the visible set
   * changes. A stale row index would point at a different control after a filter or search
   * change — harmless while this surface is read-only, a wrong-record bug the moment bulk
   * actions land. Fixed here so bulk cannot inherit it.
   */
  const patch = (next: Partial<Record<keyof typeof CONTROL_KEY, string>>, keepSelection = false) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      const key = CONTROL_KEY[k as keyof typeof CONTROL_KEY];
      if (v) p.set(key, v); else p.delete(key);
    }
    if (!keepSelection) p.delete(CONTROL_KEY.sel);
    setParams(p, { replace: true });
  };

  const selected = filtered.find((c) => c.implementationId === selectedId) ?? null;
  const naCount = controls.filter((c) => c.applicability === 'not_applicable').length;
  const filtersOn = framework !== '' || fn !== '' || query !== '';

  // Returning from the full-page view: bring the selected row back into view.
  const selectedRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Optional call: scrollIntoView is absent in some environments (jsdom, older embedded
    // webviews). Restoring position is a convenience, never a correctness requirement.
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedId]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-[12px]">
        <Skeleton className="h-[30px] w-[260px]" />
        <Skeleton className="h-[92px] rounded-[10px]" />
        <Skeleton className="h-[460px] rounded-[10px]" />
      </div>
    );
  }

  // ⚠ ABSENT vs BROKEN. A failed request is an error; an empty workspace is not.
  if (isError) {
    return (
      <div className={cn(PANEL, 'flex flex-col items-center gap-[7px] px-6 py-[52px] text-center')}>
        <span className="text-[13px] font-semibold text-text-muted">Controls could not be loaded</span>
        <span className="max-w-[44ch] text-[12px] leading-[1.6] text-text-faint">
          This is a problem retrieving your data, not a statement about it.
        </span>
      </div>
    );
  }

  const summary = data!.summary;
  const fieldBase = 'h-[28px] rounded-[6px] border border-cert-neutral-border bg-bg-surface text-[11.5px] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-sky';
  const selectCls = (active: boolean) => cn(
    fieldBase, 'cursor-pointer px-[8px] font-semibold',
    active ? 'border-cert-info-border bg-cert-info-bg text-cert-info-fg' : 'text-text-muted hover:border-text-faint hover:text-text-primary',
  );

  return (
    <div className="flex w-full flex-col gap-[12px]">
      <header className="flex flex-wrap items-start justify-between gap-[12px]">
        <div className="flex min-w-0 flex-col gap-[5px]">
          <div className="flex flex-wrap items-baseline gap-[10px]">
            <h1 className="text-[20px] font-bold leading-none text-text-primary">Controls</h1>
            <span className="text-[11.5px] tabular-nums text-text-secondary">
              {summary.controlCount === 0
                ? 'no controls in scope'
                : <>{summary.controlCount} controls in scope · showing the frameworks enabled for this view</>}
            </span>
          </div>
          <p className="max-w-[104ch] text-[11.5px] leading-[1.6] text-text-muted">
            Each control is worked once and counts for every framework that requires it. A control that
            has not been assessed is shown as exactly that — never as passing, never as failing.
          </p>
        </div>
      </header>

      {summary.controlCount === 0 ? (
        /* ⚠ EMPTY STATE ONE — nothing enabled. Truthful "nothing assessed", not an all-clear. */
        <div className={cn(PANEL, 'flex flex-col items-center gap-[8px] px-6 py-[56px] text-center')}>
          <Info className="h-[18px] w-[18px] text-text-faint" />
          <span className="text-[13px] font-semibold text-text-muted">No controls in your workspace yet</span>
          <span className="max-w-[48ch] text-[12px] leading-[1.6] text-text-faint">
            Nothing has been assessed. This is not a clean result — it means no framework has been
            enabled for this view yet, so there is nothing to report on.
          </span>
        </div>
      ) : (
        <>
          <CoverageBand summary={summary} naCount={naCount} />

          <div className="flex flex-wrap items-center gap-[8px]">
            <div className="relative min-w-[240px] flex-1 sm:max-w-[440px]">
              <Search className="pointer-events-none absolute left-[9px] top-1/2 h-[12px] w-[12px] -translate-y-1/2 text-text-faint" />
              <input
                type="search"
                value={query}
                onChange={(e) => patch({ q: e.target.value })}
                placeholder="Search reference, title or description…"
                aria-label="Search controls"
                className={cn(fieldBase, 'w-full pl-[28px] pr-[9px] text-text-primary placeholder:text-text-faint hover:border-text-faint')}
              />
            </div>

            <select aria-label="Filter by function" value={fn}
              onChange={(e) => patch({ fn: e.target.value })} className={selectCls(fn !== '')}>
              <option value="">Function</option>
              {functions.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>

            <select aria-label="Filter by framework" value={framework}
              onChange={(e) => patch({ fw: e.target.value })} className={selectCls(framework !== '')}>
              <option value="">Framework</option>
              {frameworks.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
            </select>

            {filtersOn && (
              <button type="button" onClick={() => patch({ q: '', fn: '', fw: '' })}
                className="text-[11px] font-semibold text-cert-info-fg transition-colors hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-sky">
                Clear
              </button>
            )}
          </div>

          <div className="grid min-h-0 items-start gap-[12px] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,27rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
            <div className={cn(PANEL, 'overflow-hidden')}>
              {/* Section label sits inside the panel — the border establishes the region. */}
              <div className="flex items-center justify-between gap-[10px] px-[16px] pb-[9px] pt-[11px]">
                <h2 className={EYEBROW}>All controls</h2>
                <span className="text-[10.5px] tabular-nums text-text-faint">
                  {filtered.length === controls.length
                    ? <>{controls.length} · sorted by reference</>
                    : <><span className="font-semibold text-text-muted">{filtered.length}</span> of {controls.length} · sorted by reference</>}
                </span>
              </div>

              {/* ⚠ EMPTY STATE TWO — filters matched nothing. Distinct from "nothing enabled",
                  and it names its own cause. */}
              {filtered.length === 0 ? (
                <div className={cn('flex flex-col items-center gap-[8px] border-t px-6 py-[52px] text-center', DIVIDER)}>
                  <span className="text-[13px] font-semibold text-text-muted">No controls match your search</span>
                  <span className="max-w-[44ch] text-[12px] leading-[1.6] text-text-faint">
                    Your workspace has {controls.length} controls; none match the current selection.
                  </span>
                  <button type="button" onClick={() => patch({ q: '', fn: '', fw: '' })}
                    className="pt-[2px] text-[11.5px] font-semibold text-cert-info-fg transition-colors hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-sky">
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className={cn('max-h-[700px] overflow-y-auto border-t', DIVIDER)}>
                  {filtered.map((row) => (
                    <Row
                      key={row.implementationId}
                      row={row}
                      selected={row.implementationId === selectedId}
                      innerRef={row.implementationId === selectedId ? selectedRef : undefined}
                      onSelect={() => patch({ sel: row.implementationId }, true)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={cn(PANEL, 'flex max-h-[772px] flex-col overflow-hidden')}>
              <div className="flex items-center justify-between gap-[10px] px-[16px] pb-[9px] pt-[11px]">
                <h2 className={EYEBROW}>Control detail</h2>
                {selected && (
                  <div className="flex items-center gap-[4px]">
                    <Link
                      to={`/compliance/controls/detail/${selected.implementationId}?${params.toString()}`}
                      title="Open full page"
                      className="flex items-center gap-[5px] rounded-[5px] border border-cert-neutral-border px-[7px] py-[2px] text-[10.5px] font-semibold text-text-muted transition-colors hover:border-cert-info-border hover:text-cert-info-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-sky"
                    >
                      <Maximize2 className="h-[10px] w-[10px]" />
                      Open full page
                    </Link>
                    <button
                      type="button" onClick={() => patch({}, false)} aria-label="Close details"
                      className="rounded-[5px] p-[4px] text-text-faint transition-colors hover:bg-bg-surface-2 hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-sky"
                    >
                      <X className="h-[13px] w-[13px]" />
                    </button>
                  </div>
                )}
              </div>

              {selected ? (
                <div className={cn('flex min-h-0 flex-1 flex-col gap-[11px] overflow-y-auto border-t p-[16px]', DIVIDER)}>
                  <ControlHeading row={selected} />
                  <AssessmentCard row={selected} />
                  <FunctionsCard row={selected} />
                  <RequirementsCard row={selected} />
                </div>
              ) : (
                <div className={cn('flex flex-col items-center justify-center gap-[8px] border-t px-5 py-[56px] text-center', DIVIDER)}>
                  <span className="text-[12.5px] font-semibold text-text-muted">Select a control</span>
                  <span className="max-w-[32ch] text-[11.5px] leading-[1.6] text-text-faint">
                    Its assessment, functions and mapped framework requirements appear here.
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
